/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef
} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import FilterEmptyState from '@ion/dls/src/filters/FilterEmptyState';
import FilterTreeChecklist, {
  FilterTreeChecklistChange,
  FilterTreeChecklistHandle,
  FilterTreeNode
} from '@ion/dls/src/filters/FilterTreeChecklist';
import {FilterGroupsAssetsIcon} from '@ion/dls/src/icons/current/FilterGroupsAssetsIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import changePage, {Route} from '@/routes/RouteDefinitions';
import {
  TrackingGroupsAssetsFilterHandleBase,
  TrackingGroupsAssetsFilterPropsBase
} from './types';
import useTrackingGroupsAssetsFilter, {MIN_SEARCH_TERM_LENGTH} from './useTrackingGroupsAssetsFilter';
import {requestTrackingFilterNavigation} from './utils';

export interface TrackingGroupsAssetsFilterHandle extends TrackingGroupsAssetsFilterHandleBase {}

export interface TrackingGroupsAssetsFilterProps extends TrackingGroupsAssetsFilterPropsBase {}

type FilterHandle = TrackingGroupsAssetsFilterHandle;
type FilterProps = TrackingGroupsAssetsFilterProps;

/**
 * Displays the groups/assets tree filter section for the tracking filter drawer.
 *
 * ```typescript
 * import TrackingGroupsAssetsFilter from './TrackingGroupsAssetsFilter';
 *
 * ...
 *
 * <TrackingGroupsAssetsFilter ref={filterRef}
 *   onSelectedCountChange={setSelectedCount} />
 * ```
 */
export const TrackingGroupsAssetsFilter = forwardRef<FilterHandle, FilterProps>(({
  onRequestNavigate,
  onSelectedCountChange,
  onSelectionChange
}, ref) => {
  const {
    applySelected,
    clear,
    discard,
    getFavoriteState,
    handleChange,
    hasChanges,
    loadFavoriteState,
    loadChildren,
    nodes,
    onSearchTermChange,
    pendingRevealNodePath,
    searchResults,
    searchResultsLoading,
    selectedIds
  } = useTrackingGroupsAssetsFilter({onSelectedCountChange, onSelectionChange});
  const treeRef = useRef<FilterTreeChecklistHandle>(null);

  useEffect(() => {
    if (pendingRevealNodePath) {
      treeRef.current?.revealNodePath(pendingRevealNodePath);
    }
  }, [pendingRevealNodePath]);

  const handleManageGroups = useCallback(function() {
    requestTrackingFilterNavigation(function() {
      changePage(Route.ADMIN_GROUPS);
    }, onRequestNavigate);
  }, [onRequestNavigate]);

  const handleManageAssets = useCallback(function() {
    requestTrackingFilterNavigation(function() {
      changePage(Route.ADMIN_ASSETS);
    }, onRequestNavigate);
  }, [onRequestNavigate]);

  useImperativeHandle(ref, () => ({
    apply : () => applySelected(),
    clear,
    discard,
    getFavoriteState,
    loadFavoriteState,
    hasChanges
  }), [
    applySelected,
    clear,
    discard,
    getFavoriteState,
    hasChanges,
    loadFavoriteState
  ]);

  return (
    <FilterTreeChecklist ref={treeRef}
      nodes={nodes as FilterTreeNode[]}
      selectedIds={selectedIds}
      collapseAllLabel={translate('filters:drawer.collapseAll')}
      loadingLabel={translate('common:states.loading')}
      searchPlaceholder={translate('filters:tracking.groupsAssets.searchPlaceholder')}
      emptyState={(
        <FilterEmptyState title={translate('filters:tracking.groupsAssets.empty.title')}
          description={translate('filters:tracking.groupsAssets.empty.description')}
          icon={<FilterGroupsAssetsIcon color={dlsColor('grey1')} />}
          actions={[{
            label   : translate('filters:tracking.groupsAssets.empty.manageGroups'),
            onClick : handleManageGroups
          }, {
            label   : translate('filters:tracking.groupsAssets.empty.manageAssets'),
            onClick : handleManageAssets
          }]} />
      )}
      showAllLabel={translate('filters:options.showAll')}
      showLessLabel={translate('filters:options.showLess')}
      searchMode='results'
      minSearchLength={MIN_SEARCH_TERM_LENGTH}
      minSearchLengthLabel={translate('filters:tracking.groupsAssets.minSearchLength', {count: MIN_SEARCH_TERM_LENGTH})}
      searchResults={searchResults}
      searchResultsLoading={searchResultsLoading}
      onLoadChildren={loadChildren}
      onSearchTermChange={onSearchTermChange}
      onChange={handleChange as (change: FilterTreeChecklistChange) => void} />
  );
});

export default memo(TrackingGroupsAssetsFilter);
